import React, { useState, useRef, useEffect } from 'react';
import { FaSearchLocation, FaTimes } from "react-icons/fa";
import { FaSpinner } from "react-icons/fa";
import type { GeocodeResult } from '../../types';
import styles from './SearchBar.module.css';

interface SearchBarProps {
  onSearch: (query: string) => Promise<GeocodeResult[]>;
  onAddressSelect?: (result: GeocodeResult) => void;
  onClear?: () => void;
  placeholder?: string;
  className?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  onAddressSelect,
  onClear,
  placeholder = "Search address or location...",
  className = ""
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [hasSelectedAddress, setHasSelectedAddress] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = async (searchQuery: string) => {
    if (searchQuery.trim().length < 3) {
      setResults([]);
      setShowResults(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const searchResults = await onSearch(searchQuery);
      setResults(searchResults);
      setShowResults(searchResults.length > 0);
      setSelectedIndex(-1);
      
      if (searchResults.length === 0) {
        setError('No addresses found. Try a more specific search.');
      }
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
      setShowResults(false);
      setError('Unable to search addresses. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const previousQuery = query;
    setQuery(value);
    setError(null); // Clear error when typing
    
    // If user modifies the text after selecting an address, reset the selection state
    if (hasSelectedAddress) {
      setHasSelectedAddress(false);
    }
    
    // Clear previous results immediately when typing
    if (value.trim().length < 3) {
      setResults([]);
      setShowResults(false);
    }
    
    // If the input is emptied, trigger the same effect as clicking X button
    if (value.trim() === '') {
      if (onClear) {
        onClear();
      }
    }
  };

  // Use useEffect for debounced search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (query.trim().length >= 3 && !hasSelectedAddress) {
        handleSearch(query);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [query, hasSelectedAddress]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showResults || results.length === 0) {
      if (e.key === 'Enter') {
        handleSearch(query);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleResultSelect(results[selectedIndex]);
        } else {
          handleSearch(query);
        }
        break;
      case 'Escape':
        setShowResults(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleResultSelect = (result: GeocodeResult) => {
    setQuery(result.address);
    setShowResults(false);
    setSelectedIndex(-1);
    setHasSelectedAddress(true);
    
    // Call the callback if provided
    if (onAddressSelect) {
      onAddressSelect(result);
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    setSelectedIndex(-1);
    setError(null);
    setHasSelectedAddress(false);
    
    // Call the clear callback if provided
    if (onClear) {
      onClear();
    }
    
    // Focus back to input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div ref={searchRef} className={`${styles.searchContainer} ${className}`}>
      <div className={styles.searchWrapper}>
        <input
          ref={inputRef}
          type="text"
          className={styles.searchBar}
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) {
              setShowResults(true);
            }
          }}
        />
        <span className={`${styles.searchIcon} ${hasSelectedAddress ? styles.searchIconWithButton : ''}`}>
          {isLoading ? (
            <FaSpinner className={styles.loadingSpinner} />
          ) : hasSelectedAddress ? (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              aria-label="Clear search"
            >
              <FaTimes />
            </button>
          ) : (
            <FaSearchLocation />
          )}
        </span>
        
        {showResults && results.length > 0 && (
          <div className={styles.searchResults}>
            {results.map((result, index) => (
              <div
                key={index}
                className={`${styles.searchResultItem} ${
                  index === selectedIndex ? styles.selected : ''
                }`}
                onClick={() => handleResultSelect(result)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className={styles.resultAddress}>{result.address}</div>
                <div className={styles.resultConfidence}>
                  Confidence: {Math.round(result.confidence * 100)}%
                </div>
              </div>
            ))}
          </div>
        )}
        
        {error && !isLoading && (
          <div className={styles.searchResults}>
            <div className={styles.searchError}>
              {error}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBar;